/**
 * The Trendy tab's date range — the one control that drives both of that tab's reads.
 *
 * ── Why a range needs a FLOOR, and why it is not cosmetic ─────────────────────────────────────
 * `GET /customer/{id}/muscle-volume` does not report what happened in the window. It reports a
 * **weekly rate**: `CustomerAnalyticsProcessor.ComputeWeeklySets` divides the window's weighted set
 * count by `spanDays / 7`, because the zones the tab plots against — MEV / MAV / MRV — are weekly
 * bands and nothing else would be comparable.
 *
 * That division has no lower guard on the server. It validates `from <= to` and a 366-day ceiling
 * (`AnalyticsDateRange`) and nothing more. So a two-day window divides by 0.286 and **multiplies
 * every muscle's set count by three and a half**: a client who did nine sets of chest across a
 * Monday and a Tuesday is reported at 32 weekly sets, the bar pins at the top of the track, and the
 * verdict beside it reads "ZA DUŻO" — an overtraining warning invented by arithmetic. The trainer
 * has no way to see that from the screen, because every number on it is internally consistent.
 *
 * Seven days is the shortest window whose weekly rate is a measurement rather than an extrapolation:
 * at exactly one week the divisor is 1 and the figure is the count itself.
 *
 * ── And a CEILING, because the backend has one ────────────────────────────────────────────────
 * 366 days, `AnalyticsDateRange.MaxSpanDays`. The same rule the measurement form follows: a value
 * this app accepts is one the server accepts, so the trainer never learns a bound from a 400.
 *
 * ── Two date questions, and only one of them is UTC ───────────────────────────────────────────
 * The contract's `DateOnly` is a wall-clock date with no instant in it, so SHIFTING one — "seven
 * days before this string" — is done by parsing it as a UTC midnight and adding whole days. That
 * arithmetic has no zone in it and no DST in it, which is the point.
 *
 * Asking what day it is TODAY is a different question with a different answer, and this module got
 * it wrong: `today` arrived as a live `Date` and went through `toISOString().slice(0, 10)`, which
 * converts to UTC first. A trainer in Warsaw opening the tab at 00:39 was handed yesterday as the
 * latest selectable end, and the default window therefore ended a day short — every night, for two
 * hours, for a whole zone. The comment three lines above this one warned about exactly that class
 * and the code below it did it anyway.
 *
 * So today is read from the LOCAL calendar fields (`shared/lib/calendarDate`), and only the shift
 * arithmetic stays UTC.
 */

import { MS_PER_DAY, parseDateOnly, toIsoDate } from '~/shared/lib/calendarDate';

/** The shortest window whose weekly rate is measured rather than extrapolated. See the header. */
export const TRENDS_MIN_WINDOW_DAYS = 7;

/** `AnalyticsDateRange.MaxSpanDays` — the backend's own ceiling, mirrored so a 400 never happens. */
export const TRENDS_MAX_WINDOW_DAYS = 366;

/** The window the tab opens on: thirty days back through today. */
export const TRENDS_DEFAULT_WINDOW_DAYS = 30;

export interface TrendsRange {
  /** `YYYY-MM-DD`. */
  readonly from: string;
  /** `YYYY-MM-DD`. */
  readonly to: string;
}

export interface TrendsRangeDeps {
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  /** Today, so "no range may end in the future" is decided rather than assumed. */
  readonly today: Date;
}

export interface TrendsRangeView {
  /** Whether both reads may be made. A refused range fetches nothing. */
  readonly valid: boolean;
  /**
   * Why the range was refused, in one sentence that includes the REASON rather than the rule.
   * `null` when valid.
   */
  readonly refusal: string | null;
  /** The window's length in days, inclusive of both ends — what the backend will divide by. */
  readonly spanDays: number;
  /** `max` for the From field: the latest start that still leaves a legal window. */
  readonly fromMax: string;
  /** `min` for the From field: as far back as the ceiling allows from the chosen end. */
  readonly fromMin: string;
  /** `min` for the To field. */
  readonly toMin: string;
  /** `max` for the To field. A range ending in the future measures a week nobody has trained. */
  readonly toMax: string;
}


/**
 * `days` on from a `DateOnly` string, as another one.
 *
 * The whole round trip is UTC and stays that way: `parse` builds a UTC midnight, whole days are
 * added to it, and the result is read back out of the UTC calendar fields. A local formatter here
 * would reintroduce the zone into arithmetic that has none — the mirror of the bug above.
 */
function shift(date: string, days: number): string {
  return new Date(parseDateOnly(date) + days * MS_PER_DAY).toISOString().slice(0, 10);
}

/**
 * The span the BACKEND will compute, in days.
 *
 * `ResolveMuscleVolumeWindow` rounds `(to - from).TotalDays`, so 1 August → 8 August is 7 and not 8.
 * This mirrors that exactly rather than counting calendar days inclusively — an off-by-one here
 * would let a six-day window through the check the whole module exists to make.
 */
export function trendsSpanDays(range: TrendsRange): number {
  const from = parseDateOnly(range.from);
  const to = parseDateOnly(range.to);
  if (Number.isNaN(from) || Number.isNaN(to)) return Number.NaN;
  return Math.round((to - from) / MS_PER_DAY);
}

/**
 * Today, and `days` back from it — the window any range bar opens on.
 *
 * One implementation, because the two surfaces differ only in the NUMBER and the arithmetic is the
 * part that can be wrong: `shift` is whole UTC days over a parsed midnight, and a surface reaching
 * for a local-calendar `addDays` instead would give a different `from` on the two nights a year the
 * zone changes offset.
 */
export function defaultDateRange(today: Date, days: number): TrendsRange {
  const to = toIsoDate(today);
  return { from: shift(to, -days), to };
}

/** Today, and `TRENDS_DEFAULT_WINDOW_DAYS` back from it. */
export function defaultTrendsRange(today: Date): TrendsRange {
  return defaultDateRange(today, TRENDS_DEFAULT_WINDOW_DAYS);
}

/**
 * Whether this range may be read, and what to say when it may not — with the FLOOR as a parameter.
 *
 * Both bounds are also handed back as `min`/`max` for the two fields, so the picker refuses most
 * illegal ranges before they are typed and this refusal is the backstop rather than the only guard
 * — a native date input can still be typed into past its own `max`.
 *
 * The floor is a parameter because it is the one rule that is genuinely per-surface: it exists here
 * because `/muscle-volume` divides by the window (see the header), and a surface whose read does no
 * such division has no reason to inherit seven days. Everything else — the 366-day ceiling, "no
 * range may end in the future", the reversed and unparseable cases, the four `min`/`max` bounds —
 * is one decision and stays one implementation. `model/surveyRange` is the second caller.
 */
export interface DateRangeBounds {
  /** The shortest window this surface accepts, as the span the bounds are computed from. */
  readonly minSpanDays: number;
  /** The longest. `AnalyticsDateRange.MaxSpanDays` wherever the backend does the dividing. */
  readonly maxSpanDays: number;
}

export function decideDateRange(
  range: TrendsRange,
  deps: TrendsRangeDeps,
  { minSpanDays, maxSpanDays }: DateRangeBounds,
): TrendsRangeView {
  const { t } = deps;
  const today = toIsoDate(deps.today);
  const span = trendsSpanDays(range);

  const refusal = Number.isNaN(span)
    ? t('web:clientDetail.trends.invalidDate')
    : span < 0
      ? t('web:clientDetail.trends.reversed')
      : span < minSpanDays
        ? t('web:clientDetail.trends.minWindow', { days: minSpanDays })
        : span > maxSpanDays
          ? t('web:clientDetail.trends.maxWindow', { days: maxSpanDays })
          : // A range ending after today dilutes the weekly rate rather than lengthening it: the
            // backend divides by the whole span, and the days nobody has trained yet are counted
            // in the divisor and contribute nothing to the numerator. Six days of real work read
            // through a fourteen-day window come back halved. `toMax` already stops the picker
            // offering one; this is the backstop, because a native date input can be TYPED past
            // its own `max` and the field then submits a value the control never offered.
            range.to > today
            ? t('web:clientDetail.trends.future')
            : null;

  return {
    valid: refusal === null,
    refusal,
    spanDays: span,
    fromMin: Number.isNaN(parseDateOnly(range.to))
      ? shift(today, -maxSpanDays)
      : shift(range.to, -maxSpanDays),
    fromMax: Number.isNaN(parseDateOnly(range.to))
      ? shift(today, -minSpanDays)
      : shift(range.to, -minSpanDays),
    toMin: Number.isNaN(parseDateOnly(range.from))
      ? shift(today, -maxSpanDays + minSpanDays)
      : shift(range.from, minSpanDays),
    toMax: today,
  };
}

/** The Trendy tab's own range: the seven-day floor this module exists for. */
export function decideTrendsRange(range: TrendsRange, deps: TrendsRangeDeps): TrendsRangeView {
  return decideDateRange(range, deps, {
    minSpanDays: TRENDS_MIN_WINDOW_DAYS,
    maxSpanDays: TRENDS_MAX_WINDOW_DAYS,
  });
}
